import { useEffect, useId, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import {
  organizationContactDataById,
  useFetchOrganizationsContactData,
} from '~/api/organizationContactData';
import { useMySellerLists, type SellerList } from '~/api/sellerLists';
import { cn, toastGraphQLError } from '~/utils';
import { sellerListSelections } from '~/components/common/sellerListSelections';
import type { SellerRecipients } from '~/components/common/useSellerRecipients';

export type SellerListTogglesLabels = {
  heading: string;
  toggle: (name: string, sellerCount: number) => string;
};

type SellerListTogglesProps = {
  recipients: Pick<
    SellerRecipients,
    'isSellerListOn' | 'turnOnSellerList' | 'turnOffSellerList'
  >;
  labels: SellerListTogglesLabels;
  testIdPrefix: string;
  disabled: boolean;
};

export function SellerListToggles({
  recipients,
  labels,
  testIdPrefix,
  disabled,
}: Readonly<SellerListTogglesProps>) {
  const labelId = useId();
  const { data } = useMySellerLists();
  const sellerLists = data?.mySellerLists ?? [];
  const fetchContactData = useFetchOrganizationsContactData();
  const [isTurningOn, setIsTurningOn] = useState(false);
  const latestRequest = useRef(0);

  useEffect(
    () => () => {
      latestRequest.current++;
    },
    []
  );

  const turnOn = async (sellerList: SellerList) => {
    const request = ++latestRequest.current;
    setIsTurningOn(true);
    try {
      const contactData = await fetchContactData(
        sellerList.members.map(({ seller }) => seller.id)
      );
      const selections = sellerListSelections(
        sellerList.members,
        organizationContactDataById(contactData)
      );
      if (request === latestRequest.current) {
        recipients.turnOnSellerList(sellerList.id, selections);
      }
    } catch (error) {
      if (request === latestRequest.current) {
        toastGraphQLError(error);
      }
    } finally {
      if (request === latestRequest.current) {
        setIsTurningOn(false);
      }
    }
  };

  const toggle = (sellerList: SellerList) => {
    if (isTurningOn) {
      return;
    }
    if (recipients.isSellerListOn(sellerList.id)) {
      recipients.turnOffSellerList(sellerList.id);
    } else {
      void turnOn(sellerList);
    }
  };

  if (sellerLists.length === 0) {
    return null;
  }

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      className="flex flex-wrap items-center gap-2"
    >
      <span id={labelId} className="text-sm text-neut-sec">
        {labels.heading}
      </span>
      {sellerLists.map((sellerList) => {
        const isListOn = recipients.isSellerListOn(sellerList.id);
        return (
          <button
            key={sellerList.id}
            type="button"
            data-testid={`${testIdPrefix}-seller-list-${sellerList.id}`}
            aria-pressed={isListOn}
            aria-disabled={isTurningOn}
            disabled={disabled || sellerList.members.length === 0}
            onClick={() => toggle(sellerList)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-prim aria-disabled:cursor-wait disabled:cursor-not-allowed disabled:opacity-50',
              isListOn
                ? 'border-prim bg-prim-50 font-medium text-prim-700'
                : 'border-neut-border bg-neut-bg-alt text-neut-prim hover:bg-neut-hover'
            )}
          >
            {isListOn && <Check size={14} aria-hidden="true" />}
            {labels.toggle(sellerList.name, sellerList.members.length)}
          </button>
        );
      })}
    </div>
  );
}
